import { Controller, useFormContext } from 'react-hook-form';
import React from 'react';
import { EqualisationSettingsFormValues } from './FormWrapper';
import { PreferredReturnAccrualMethod } from 'further-types/lp/carry-settings';
import { AppNumberInput, FieldRow } from 'components/FormElements';
import { TextField } from '@mui/material';
import AppCheckbox from 'components/FormElements/AppCheckBox';
import useCarriedInterestFeeTemplatesOptions from '../CarrySettings/CarriedInterestFeeTemplates/hooks/useCarriedInterestFeeTemplatesOptions';
import BasicSelect from 'further-ui/components/BasicSelect';

const EqualisationInterestFeeTemplateFields: React.FC<{
  index: number;
}> = ({ index }) => {
  const { watch, control, setValue } =
    useFormContext<EqualisationSettingsFormValues>();
  const {
    preferredReturnAccrualMethodOptions:
      equalisationInterestAccrualMethodOptions,
    preferredReturnCompoundingPeriodOptions:
      equalisationInterestCompoundingPeriodOptions,
    preferredReturnBasisOptions: equalisationInterestBasisOptions,
  } = useCarriedInterestFeeTemplatesOptions();

  const isCompoundAccrualMethod =
    watch(
      `equalisationInterestFeeTemplates.${index}.equalisationInterestAccrualMethod`,
    ) === PreferredReturnAccrualMethod.Compound;

  return (
    <>
      <FieldRow title="Template name">
        <Controller
          name={`equalisationInterestFeeTemplates.${index}.name`}
          control={control}
          render={({ fieldState: { error }, field }) => (
            <TextField
              size="small"
              {...field}
              fullWidth
              error={!!error}
              helperText={error?.message}
            />
          )}
        />
      </FieldRow>
      <FieldRow
        title="Equalisation interest rate"
        centerTitle
        tooltipText="This field may be left blank and input at equalisation (in the Close & Equalise feature) if the fund vehicle uses a floating rate (e.g. SONIA), which means the rate will be unknown until the point of equalisation."
      >
        <Controller
          name={`equalisationInterestFeeTemplates.${index}.equalisationInterestRate`}
          control={control}
          render={({ fieldState: { error }, field: { onChange, ...rest } }) => (
            <AppNumberInput
              fullWidth
              {...rest}
              decimals={10}
              minFractionDigits={2}
              onChange={(value: number | null) => {
                onChange(value ?? 0);
              }}
              error={!!error}
              helperText={error?.message}
              percentEndAdornment
              zeroAsPlaceholder
            />
          )}
        />
      </FieldRow>
      <FieldRow title="Equalisation interest accrual method" centerTitle>
        <Controller
          name={`equalisationInterestFeeTemplates.${index}.equalisationInterestAccrualMethod`}
          control={control}
          render={({ fieldState: { error }, field: { value, onChange } }) => (
            <BasicSelect
              options={equalisationInterestAccrualMethodOptions}
              fullWidth
              error={!!error}
              helperText={error?.message}
              value={value}
              onChange={(newValue) => {
                onChange(newValue);
                if (newValue === PreferredReturnAccrualMethod.Simple) {
                  setValue(
                    `equalisationInterestFeeTemplates.${index}.equalisationInterestCompoundingPeriod`,
                    null,
                  );
                }
              }}
            />
          )}
        />
      </FieldRow>
      {isCompoundAccrualMethod && (
        <FieldRow
          title="Equalisation interest compounding period"
          centerTitle
          tooltipText="This is the period after which equalisation interest compounds and is considered within the basis of further equalisation interest calculations. For example, a monthly compounding period means that at the end of each month equalisation interest is added to the principal for further interest calculations, but in the middle of the month the system's daily calculation will take the principal and interest sum from the end of the previous month."
        >
          <Controller
            name={`equalisationInterestFeeTemplates.${index}.equalisationInterestCompoundingPeriod`}
            control={control}
            render={({ fieldState: { error }, field: { onChange, value } }) => (
              <BasicSelect
                options={equalisationInterestCompoundingPeriodOptions}
                fullWidth
                error={!!error}
                helperText={error?.message}
                value={value ?? undefined}
                onChange={onChange}
              />
            )}
          />
        </FieldRow>
      )}
      <FieldRow title="Equalisation interest basis" centerTitle>
        <Controller
          name={`equalisationInterestFeeTemplates.${index}.equalisationInterestBasis`}
          control={control}
          render={({ fieldState: { error }, field: { onChange, value } }) => (
            <BasicSelect
              options={equalisationInterestBasisOptions}
              fullWidth
              error={!!error}
              helperText={error?.message}
              value={value}
              onChange={onChange}
            />
          )}
        />
      </FieldRow>
      <FieldRow title="Default equalisation interest setting" centerTitle>
        <Controller
          name={`equalisationInterestFeeTemplates.${index}.isDefault`}
          control={control}
          render={({ fieldState: { error }, field: { onChange, value } }) => (
            <AppCheckbox
              checked={!!value}
              onChange={(e) => {
                const newValue = e.target.checked;
                onChange(newValue);
                if (newValue) {
                  const allTemplates = watch(
                    'equalisationInterestFeeTemplates',
                  );
                  allTemplates.forEach((_, idx) => {
                    if (idx !== index) {
                      setValue(
                        `equalisationInterestFeeTemplates.${idx}.isDefault`,
                        false,
                      );
                    }
                  });
                }
              }}
              error={!!error}
              helperText={error?.message}
              disabled={false}
            />
          )}
        />
      </FieldRow>
    </>
  );
};

export default EqualisationInterestFeeTemplateFields;
